import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../../app/hooks'
import { getSettings } from '../../../services/ValidationService'
import { assignReferences, noop, RunOnceEffect } from '../../../utils/Utils'
import { FieldProperties, FormMode } from '../../types'
import FieldLabel from '../field-label/FieldLabel'
import { GuslFormState } from '../maintain-form/guslFormSlice'
import { FieldContentStyled, FieldContentWrapperStyled, FloatingFormStyled, HintStyled } from '../text/styled'
import { BrowseLinkStyled, DropZoneHintStyled, DropZoneStyled, HiddenFileInputStyled, SelectedFileStyled } from './styled'

const formatFileSize = (bytes: number): string => {
    if (!bytes) {
        return ''
    }
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIdx = 0
    while (size >= 1024 && unitIdx < units.length - 1) {
        size = size / 1024
        unitIdx++
    }
    return `${size.toFixed(unitIdx === 0 ? 0 : 1)} ${units[unitIdx]}`
}

export const FileUploadField = (properties: FieldProperties): React.ReactElement<FieldProperties> => {
    const _guslFormState: GuslFormState = useAppSelector((state) => state.guslFormSlice[properties.code])
    const [formMode, setFormMode] = useState(properties.formMode)
    const [formValue, setFormValue] = useState<File | undefined>(_guslFormState?.getFieldValue(properties) || properties.data || undefined)

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [submitted, setSubmitted] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    // native drag events fire for every child element, so keep a depth counter
    const dragCounter = useRef<number>(0)

    const onFormModeChange = (mode: FormMode) => {
        setFormMode(mode)
    }

    useEffect(() => {
        setFormValue(_guslFormState?.getFieldValue(properties) || properties.data || undefined)
    }, [properties])

    const doValidation = (fieldValue: any): boolean => {
        setSubmitted(true)
        if (fieldValue) {
            return true
        }
        setErrorMessage('No file selected')
        // vvv not sure why formvalue is null
        return true // false;
    }

    RunOnceEffect(() => {
        assignReferences(properties.reference, onFormModeChange, noop, doValidation)
    })

    const renderTableView = (): React.ReactElement => {
        return <></>
    }

    const onFileSelected = (file: File | undefined) => {
        setSubmitted(false)
        setErrorMessage(undefined)
        setFormValue(file)
        properties.onChange(properties.fieldConfig.name, file)
    }

    const onDataInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            onFileSelected(e.target.files[0])
        }
    }

    const openFileDialog = (disableField: boolean) => {
        if (disableField) {
            return
        }
        fileInputRef.current?.click()
    }

    const onDragEnter = (e: DragEvent<HTMLDivElement>, disableField: boolean) => {
        e.preventDefault()
        e.stopPropagation()
        if (disableField) {
            return
        }
        dragCounter.current++
        setIsDragging(true)
    }

    const onDragOver = (e: DragEvent<HTMLDivElement>, disableField: boolean) => {
        // required, otherwise the browser opens the file instead of dropping it
        e.preventDefault()
        e.stopPropagation()
        if (!disableField) {
            setIsDragging(true)
        }
    }

    const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounter.current--
        if (dragCounter.current <= 0) {
            dragCounter.current = 0
            setIsDragging(false)
        }
    }

    const onDrop = (e: DragEvent<HTMLDivElement>, disableField: boolean) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounter.current = 0
        setIsDragging(false)
        if (disableField) {
            return
        }
        const files = e.dataTransfer?.files
        if (files && files.length > 0) {
            onFileSelected(files[0])
            // keep the hidden input in sync so re-selecting the same file still fires onChange
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const onRemoveFile = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        onFileSelected(undefined)
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, disableField: boolean) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openFileDialog(disableField)
        }
    }

    const renderFormView = (): React.ReactElement => {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        const [hideField, disableField, required] = getSettings(formMode, properties.fieldConfig, formValue)
        const hasError = submitted && !!errorMessage
        return (
            <>
                {!hideField && (
                    <div className="m-1">
                        <FloatingFormStyled>
                            <FieldContentWrapperStyled>
                                <FieldContentStyled></FieldContentStyled>
                                <HiddenFileInputStyled
                                    type={'file'}
                                    ref={fileInputRef}
                                    id={properties?.fieldConfig?.name || 'def'}
                                    key={properties?.fieldConfig?.name || 'def'}
                                    onChange={(e) => onDataInputChange(e)}
                                    disabled={disableField}
                                    required={required}
                                />
                                <DropZoneStyled
                                    isDragging={isDragging}
                                    hasError={hasError}
                                    disabled={!!disableField}
                                    role={'button'}
                                    tabIndex={disableField ? -1 : 0}
                                    onClick={() => openFileDialog(!!disableField)}
                                    onKeyDown={(e) => onKeyDown(e, !!disableField)}
                                    onDragEnter={(e) => onDragEnter(e, !!disableField)}
                                    onDragOver={(e) => onDragOver(e, !!disableField)}
                                    onDragLeave={(e) => onDragLeave(e)}
                                    onDrop={(e) => onDrop(e, !!disableField)}
                                >
                                    <i className="fa-solid fa-cloud-arrow-up" />
                                    <div>
                                        {isDragging ? (
                                            <span>Drop the file here</span>
                                        ) : (
                                            <span>
                                                Drag and drop a file here, or <BrowseLinkStyled>browse</BrowseLinkStyled>
                                            </span>
                                        )}
                                    </div>
                                    {!formValue && <DropZoneHintStyled>No file selected</DropZoneHintStyled>}
                                    {formValue && (
                                        <SelectedFileStyled>
                                            <span className="file-name">{formValue.name}</span>
                                            {!!formValue.size && <span className="file-size">({formatFileSize(formValue.size)})</span>}
                                            {!disableField && (
                                                <i
                                                    className="fa-solid fa-xmark remove-file"
                                                    title={'Remove file'}
                                                    onClick={(e) => onRemoveFile(e)}
                                                />
                                            )}
                                        </SelectedFileStyled>
                                    )}
                                </DropZoneStyled>
                            </FieldContentWrapperStyled>
                            {properties.fieldConfig.hint && <HintStyled className="px-2">{properties.fieldConfig.hint}</HintStyled>}
                            {submitted && errorMessage && <small className="yellow">{errorMessage}</small>}
                            <FieldLabel properties={properties} />
                        </FloatingFormStyled>
                    </div>
                )}
            </>
        )
    }

    return <>{properties.isTableView ? renderTableView() : renderFormView()}</>
}
